import Login from "./pages/login/Login.jsx";
import React, { useState, useEffect, useLayoutEffect, Suspense } from 'react';
import { Outlet, RouterProvider, createBrowserRouter,useNavigate, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ThemeProvider } from "./contexts/Theme-Context.js";
import { AuthProvider } from "./contexts/Auth-Context";
import { SymbolProvider } from "./contexts/Symbol-Context";
import { AccountManagerProvider } from "./contexts/Account-Manager-Context";
import { ChartProvider } from "./contexts/Chart-Context";
import { MetricsProvider } from "./contexts/Metrics-Context.js";
import { RippleProvider } from "./contexts/Ripple-Context.js";
import Main from "./pages/main/Main.jsx";
import Symbol from "./components/main/symbol/Symbol";
import Chart from "./components/main/chart/Chart";
import OrderPanel from "./components/main/order_panel/Order-Panel";
import OrderBook from "./components/main/order_book/Order-Book";
import AccountManager from "./components/main/account_manager/Account-Manager";
import UserSettings from "./components/main/user_settings/UserSettings";
import MetricsPanel from "./components/main/metrics_panel/Metrics-Panel.jsx";
import "./App.scss";
import { OrderProvider } from "./contexts/Order-Context.js";
import 'react-toastify/dist/ReactToastify.css';
import { PromotionProvider } from "./contexts/promotionContext.js";
import Loader from '../src/components/utils/Loader/Loader.jsx'
import getBackendUrl, { getBackendPic, getMobileV2RedirectUrl } from "./components/utils/RedirectUrl.js";
import { resolveClientPublicIp } from "./utils/manualActionAudit";
import { useChartContext } from "./contexts/Chart-Context.js";


const Layout = () => {
  const navigate = useNavigate();
  const { selectedStyle } = useChartContext();
  const styleVars = {
    '--buy-color': selectedStyle.buyColor ?? selectedStyle.colors?.[0],
    '--sell-color': selectedStyle.sellColor ?? selectedStyle.colors?.[1],
  };

  // Keep accent tokens on <html> so portaled menus (body) still resolve dynamic Style Settings colors.
  useLayoutEffect(() => {
    const root = document.documentElement;
    Object.entries(styleVars).forEach(([key, value]) => {
      if (value) root.style.setProperty(key, value);
      else root.style.removeProperty(key);
    });
  }, [styleVars['--buy-color'], styleVars['--sell-color']]);

  useEffect(() => {
    try {
      const currentUrl = new URL(window.location.href);
      const pathname = currentUrl.pathname;

      const isDepositResultPath =
        pathname === '/deposit/result' ||
        pathname === '//deposit/result' ||
        pathname.endsWith('/deposit/result');

      if (isDepositResultPath) {
        const status = (currentUrl.searchParams.get('status') || '').toLowerCase();
        const rawMessage = currentUrl.searchParams.get('message') || '';

        // Clean message: if it comes as "[...inside...] actual text",
        // strip the bracketed part and keep only the readable message.
        let message = rawMessage;
        const bracketMatch = rawMessage.match(/^\[[^\]]*]\s*(.*)$/);
        if (bracketMatch && bracketMatch[1]) {
          message = bracketMatch[1];
        }

        if (status === 'declined' || status === 'failed') {
          toast.error(message || 'Card payment declined', {
            position: 'top-right',
          });
        } else if (status === 'success') {
          toast.success(message || 'Card payment successful', {
            position: 'top-right',
          });
        }
      }
    } catch (e) {
      // no-op if URL parsing fails
    }

    const params = new URLSearchParams(window.location.search);
    const hasDirectLoginToken = params.has('token');

    if (hasDirectLoginToken) {
      if (window.location.pathname !== '/') {
        navigate('/', { replace: true });
      }
      return;
    }

    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');

    if (userId && token) {
      navigate('/home');
    } else {
      navigate('/');
    }
  }, []);

  return (
    <div style={styleVars}>
      <Outlet />
    </div>
  );
};

// Minimal route component for /deposit/result so React Router
// doesn't show its default 404 page while we handle the toast + redirect in Layout.
const DepositResult = () => {
  return null;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true, // Use "index: true" for the root path
        element: <Login />, // Render the Login component for the root path    
      },
      {
        path: "home", // Use "home" for the "/home" path
        element: <Main />, // Render the Main component for the "/home" path
      },
      {
        path: "deposit/result",
        element: <DepositResult />,
      },
      {
        path: "/",
        element: <Main />,
        children: [
          { path: "Symbol", element: <Symbol /> },
          { path: "Chart", element: <Chart /> },
          { path: "Order-Panel", element: <OrderPanel /> },
          { path: "Order-Book", element: <OrderBook /> },
          { path: "Account-Manager", element: <AccountManager /> },
          { path: "UserSettings", element: <UserSettings /> },
          { path: "Login", element: <Login /> },
          { path: "MetricsPanel", element: <MetricsPanel /> },
        ],
      },
    ],
  },
]);

function App() {
  // Never skip the mobile loader just because ?token= is present — that used to mount
  // Login, consume the token, then redirect to app with an already-cleared token.
  const [loading, setLoading] = useState(() => window.innerWidth <= 960);
  const [isMobile, setIsMobile] = useState(false);
  // Base URL for the mobile (Expo/web) app
  const mobileBaseUrl = process.env.REACT_APP_MOBILE_URL || 'http://localhost:8081';
  // Backend URL (domain) derived from current host
  const backendUrl = getBackendUrl();
  // Final URL loaded in the iframe on mobile, with backend/domain passed as query param
  const url = `${mobileBaseUrl}?backend=${encodeURIComponent(backendUrl)}`;

  useEffect(() => {
    void resolveClientPublicIp();
  }, []);

  useEffect(() => {
    const favicon = document.querySelector("link[rel~='icon']");
    const isMobileScreen = window.innerWidth <= 960;
    setIsMobile(isMobileScreen);
    if (favicon) {
      favicon.href = getBackendUrl() + `/assets/admin/images/${getBackendPic()}favicon.png`;
    }

    if (isMobileScreen) {
      const v2Url = getMobileV2RedirectUrl();
      console.log("v2Url", v2Url);
      if (v2Url) {
        window.location.replace(v2Url);
        return;
      }
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000); // Show for 2 seconds

      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, []);

  // Belt-and-suspenders: if bootstrap missed mobile handoff, redirect before painting Login
  if (typeof window !== 'undefined' && window.innerWidth <= 960) {
    const v2Url = getMobileV2RedirectUrl();
    if (v2Url) {
      window.location.replace(v2Url);
      return <Loader />;
    }
  }

  
  if (loading) 
    return <Loader />;

  // On small screens, embed the Expo web app instead of rendering the full trade-client UI
  // if (isMobile) {
  //   return (
  //     <div className="App" style={{ width: '100%', height: '100vh', overflow: 'hidden' }}>
  //       <iframe
  //         src={url}
  //         title="Trade Mobile"
  //         // allow="notifications"
  //         sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
  //         allow="notifications; push;"        
  //         style={{ border: 'none', width: '100%', height: '100%' }}
  //       />
  //     </div>
  //   );
  // }

  return (
    <ThemeProvider>
      <RippleProvider>
        <AuthProvider>
          <PromotionProvider>
            <AccountManagerProvider>
              <ChartProvider>
                <SymbolProvider>
                  <MetricsProvider>
                    <OrderProvider>
                      <div className="App">
                        <RouterProvider router={router} />
                      </div>
                    </OrderProvider>
                  </MetricsProvider>
                </SymbolProvider>
              </ChartProvider>
            </AccountManagerProvider>
          </PromotionProvider>
        </AuthProvider>
      </RippleProvider>
    </ThemeProvider>
  );
}

export default App;
